import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { Box, CircularProgress, Alert } from "@mui/material";
import { toast } from "react-toastify";
import {
  useStartExerciseSessionMutation,
  useSubmitExerciseAnswerMutation,
  useCompleteExerciseSessionMutation,
  useLazyGetExerciseSessionQuery,
} from "@/store/api/user/exerciseApi";
import FinalScoreDialog from "./FinalScoreDialog";
import LCExerciseSession from "./LCExerciseSession";
import RCExerciseSession from "./RCExerciseSession";

const EXERCISE_DRAFT_PREFIX = "toeic.exerciseDraft.v1.";

const exerciseDraftKey = (sessionId) => `${EXERCISE_DRAFT_PREFIX}${sessionId}`;

const loadExerciseDraft = (sessionId) => {
  if (!sessionId) return null;
  try {
    const raw = localStorage.getItem(exerciseDraftKey(sessionId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.answers && typeof parsed.answers === "object"
      ? parsed.answers
      : null;
  } catch (_) {
    return null;
  }
};

const saveExerciseDraft = (sessionId, answers) => {
  if (!sessionId) return;
  try {
    localStorage.setItem(
      exerciseDraftKey(sessionId),
      JSON.stringify({ answers: answers || {}, savedAt: new Date().toISOString() })
    );
  } catch (_) {
    // ignore localStorage failures
  }
};

const clearExerciseDraft = (sessionId) => {
  if (!sessionId) return;
  try {
    localStorage.removeItem(exerciseDraftKey(sessionId));
  } catch (_) {
    // ignore
  }
};

const isMeaningfulAnswer = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim() !== "";
  if (Array.isArray(value)) return value.some((v) => isMeaningfulAnswer(v));
  if (typeof value === "object") {
    const entries = Object.values(value);
    if (entries.length === 0) return false;
    return entries.some((v) => isMeaningfulAnswer(v));
  }
  return true;
};

const buildDialogueMaps = (parts) => {
  const parentIds = new Set();
  const childToParent = {};

  (parts || []).forEach((part) => {
    (part.questionGroups || []).forEach((group) => {
      const parent = (group.questions || []).find(
        (q) => q?.type === "dialogue-group"
      );
      if (!parent?._id) return;

      const parentId = String(parent._id);
      parentIds.add(parentId);
      const children = group.childQuestions || parent.childQuestions || [];
      children.forEach((child) => {
        if (child?._id) childToParent[String(child._id)] = parentId;
      });
    });
  });

  return { parentIds, childToParent };
};

const buildRestoredAnswers = (parts, responses = []) => {
  const { parentIds, childToParent } = buildDialogueMaps(parts);
  const restored = {};

  (responses || []).forEach((r) => {
    const questionId = String(
      r?.questionId || r?.question?._id || r?.question || ""
    );
    if (!questionId) return;

    const selectedOption = r?.selectedOption;
    if (parentIds.has(questionId)) {
      const key = `dialogue_${questionId}`;
      const prev = restored[key] || {};
      restored[key] = {
        ...prev,
        blanks:
          selectedOption && typeof selectedOption === "object"
            ? selectedOption
            : {},
        childQuestions: prev.childQuestions || {},
      };
      return;
    }

    const parentId = childToParent[questionId];
    if (parentId) {
      const key = `dialogue_${parentId}`;
      const prev = restored[key] || {};
      restored[key] = {
        ...prev,
        blanks: prev.blanks || {},
        childQuestions: {
          ...(prev.childQuestions || {}),
          [questionId]: selectedOption,
        },
      };
      return;
    }

    restored[questionId] = selectedOption;
  });

  return restored;
};

const mergeAnswersForResume = (serverAnswers = {}, localAnswers = {}) => {
  const merged = { ...serverAnswers };

  Object.entries(localAnswers || {}).forEach(([key, value]) => {
    if (key.startsWith("dialogue_")) {
      const prev = merged[key] || {};
      merged[key] = {
        ...prev,
        ...(value || {}),
        blanks: {
          ...(prev.blanks || {}),
          ...((value && value.blanks) || {}),
        },
        childQuestions: {
          ...(prev.childQuestions || {}),
          ...((value && value.childQuestions) || {}),
        },
      };
      return;
    }
    merged[key] = value;
  });

  return merged;
};

const findResumePositionByAnswers = (parts, restoredAnswers = {}) => {
  const { parentIds, childToParent } = buildDialogueMaps(parts);

  const hasAnswered = (qid) => {
    if (parentIds.has(qid)) {
      const dialogue = restoredAnswers[`dialogue_${qid}`] || {};
      return isMeaningfulAnswer(dialogue.blanks) || isMeaningfulAnswer(restoredAnswers[qid]);
    }

    const parentId = childToParent[qid];
    if (parentId) {
      const dialogue = restoredAnswers[`dialogue_${parentId}`] || {};
      return (
        isMeaningfulAnswer(dialogue.childQuestions?.[qid]) ||
        isMeaningfulAnswer(restoredAnswers[qid])
      );
    }

    return isMeaningfulAnswer(restoredAnswers[qid]);
  };

  for (let partIndex = 0; partIndex < (parts || []).length; partIndex++) {
    const groups = parts[partIndex]?.questionGroups || [];
    for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
      const group = groups[groupIndex] || {};
      const ids = [
        ...(group.questions || []).map((q) => String(q?._id || "")).filter(Boolean),
        ...(group.childQuestions || [])
          .map((q) => String(q?._id || ""))
          .filter(Boolean),
      ];

      const allAnswered = ids.length > 0 && ids.every((qid) => hasAnswered(qid));
      if (!allAnswered) {
        return { partIndex, groupIndex };
      }
    }
  }

  const lastPartIndex = Math.max(0, (parts || []).length - 1);
  const lastGroups = parts?.[lastPartIndex]?.questionGroups || [];
  return {
    partIndex: lastPartIndex,
    groupIndex: Math.max(0, lastGroups.length - 1),
  };
};

const ExerciseSessionInline = ({
  dayId,
  sectionType,
  sectionInfo,
  dayLabel,
  onExit,
}) => {
  const questionStartTimeRef = useRef({});
  const answersRef = useRef({}); // Keep latest answers in ref to avoid stale state
  const [currentPartIndex, setCurrentPartIndex] = useState(0);
  const [currentGroupIndex, setCurrentGroupIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [openFinalScoreDialog, setOpenFinalScoreDialog] = useState(false);
  const [finalScore, setFinalScore] = useState(null);
  const [showInstructions, setShowInstructions] = useState(true);
  const [seenPartInstructions, setSeenPartInstructions] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [sessionStarted, setSessionStarted] = useState(false);
  const [sectionData, setSectionData] = useState(null);
  const [sessionError, setSessionError] = useState(null);
  // Group questions by part - matching Exam session format
  const parts = useMemo(() => {
    if (!sectionData?.parts) return [];
    
    const mappedParts = sectionData.parts.map(part => {
      // Merge part with partInstruction to get audioUrl, imageUrl, instructions
      return {
        ...part,
        ...(part.partInstruction || {}),
        // Ensure partNumber is available
        partNumber: part.partNumber || part.number,
      };
    });
    
    return mappedParts;
  }, [sectionData]);

  const currentPart = useMemo(() => {
    return parts?.[currentPartIndex] || null;
  }, [parts, currentPartIndex]);
  // Determine if current part is listening or reading
  // For attendance test, Parts 1-4 are LC, Parts 5-7 are RC
  const isListening = useMemo(() => {
    if (sectionType === "lcExercise") return true;
    if (sectionType === "rcExercise") return false;
    // For attendance test, check current part number
    if (sectionType === "attendanceTest") {
      const partNumber = currentPart?.partNumber || currentPart?.number;
      return partNumber >= 1 && partNumber <= 4;
    }
    return false;
  }, [sectionType, currentPart]);

  const [startSession] = useStartExerciseSessionMutation();
  const [submitExerciseAnswer] = useSubmitExerciseAnswerMutation();
  const [completeSession] = useCompleteExerciseSessionMutation();
  const [getExerciseSession] = useLazyGetExerciseSessionQuery();

  const questionGroup = useMemo(() => {
    return currentPart?.questionGroups || [];
  }, [currentPart]);

  const currentGroup = useMemo(() => {
    return questionGroup?.[currentGroupIndex] || null;
  }, [questionGroup, currentGroupIndex]);

  // Track start time for questions in current group
  useEffect(() => {
    if (currentGroup?.questions && !showInstructions) {
      currentGroup.questions.forEach((q) => {
        if (!questionStartTimeRef.current[q._id]) {
          questionStartTimeRef.current[q._id] = Date.now();
        }
      });
    }
  }, [currentGroup, showInstructions]);

  // Sync answersRef when answers state changes
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  // Start session when component mounts
  useEffect(() => {
    const initializeSession = async () => {
      if (!sessionStarted && dayId && sectionType) {
        try {
          setSessionError(null);
          // Convert sectionType to API format
          const section =
            sectionType === "lcExercise"
              ? "lc-exercise"
              : sectionType === "rcExercise"
              ? "rc-exercise"
              : "attendance-test";

          // Get device info
          const deviceInfo = {
            browser: navigator.userAgent,
            os: navigator.platform,
            device: /Mobile|Android|iPhone|iPad|iPod/i.test(navigator.userAgent)
              ? "Mobile"
              : "Desktop",
          };

          const response = await startSession({
            dayId,
            section,
            deviceInfo,
          }).unwrap();

          const attempt = response?.data || {};
          const sectionPayload = response?.sectionData || null;

          setSessionId(attempt.sessionId);
          setSessionStarted(true);
          // Store section data with part instructions
          if (sectionPayload) {
            setSectionData(sectionPayload);
          }

          // Resume from the first unfinished group when server says this is
          // an existing in-progress session.
          if (response?.isResume && sectionPayload?.parts) {
            let resumeResponses = attempt.responses || [];

            // Use the dedicated session endpoint to get normalized response
            // objects (`questionId`) and avoid id-shape mismatches.
            try {
              const detail = await getExerciseSession(attempt.sessionId).unwrap();
              if (Array.isArray(detail?.data?.responses)) {
                resumeResponses = detail.data.responses;
              }
            } catch (_) {
              // Fallback to start-session payload.
            }

            const restored = buildRestoredAnswers(
              sectionPayload.parts,
              resumeResponses
            );
            const localDraftAnswers = loadExerciseDraft(attempt.sessionId) || {};
            const mergedAnswers = mergeAnswersForResume(restored, localDraftAnswers);
            setAnswers(mergedAnswers);
            answersRef.current = mergedAnswers;

            const resumePos = findResumePositionByAnswers(
              sectionPayload.parts,
              mergedAnswers
            );
            setCurrentPartIndex(resumePos.partIndex);
            setCurrentGroupIndex(resumePos.groupIndex);
            setShowInstructions(false);
            const partNumber =
              sectionPayload.parts?.[resumePos.partIndex]?.partNumber ||
              sectionPayload.parts?.[resumePos.partIndex]?.number;
            setSeenPartInstructions(
              partNumber ? [partNumber] : []
            );
            toast.info("Resuming your previous session");
          }
        } catch (error) {
          console.error("Failed to start session:", error);
          setSessionError(error);
          toast.error("Failed to start exercise session");
        }
      }
    };

    initializeSession();
  }, [dayId, sectionType, sessionStarted, startSession, getExerciseSession]);

  // Persist latest in-progress answers locally so resume also restores
  // answers typed but not yet flushed to server.
  useEffect(() => {
    if (!sessionId || !sessionStarted) return;
    saveExerciseDraft(sessionId, answers);
  }, [sessionId, sessionStarted, answers]);

  const handleSubmitAnswer = useCallback(async (questionId, answer) => {
    const timeSpent = Math.floor(
      (Date.now() - (questionStartTimeRef.current[questionId] || Date.now())) /
        1000
    );

    await submitExerciseAnswer({
      sessionId,
      questionId,
      selectedOption: answer,
      timeSpent,
    }).unwrap();
  }, [sessionId, submitExerciseAnswer]);

  const handleAnswerSelect = useCallback(async (questionId, answer, questionType) => {
    setAnswers((prev) => {
      const updated = {
        ...prev,
        [questionId]: answer,
      };
      // Keep ref in sync with state for immediate access
      answersRef.current = updated;
      return updated;
    });

    // Track question start time if not already tracked
    if (!questionStartTimeRef.current[questionId]) {
      questionStartTimeRef.current[questionId] = Date.now();
    }

    // Only submit immediately for multiple-choice questions
    // For fill-blank and short_answer, submit when moving to next question
    if (sessionId && questionType === "multiple-choice") {
      try {
        await handleSubmitAnswer(questionId, answer);
      } catch (error) {
        console.error("Failed to submit answer:", error);
        // Don't show error to user, just log it
      }
    }
  }, [sessionId, handleSubmitAnswer]);

  const handleFinishExercise = useCallback(async () => {
    if (!sessionId) {
      toast.error("No active session found");
      return;
    }
    try {
      // Complete session via API
      const response = await completeSession(sessionId).unwrap();

      setFinalScore({
        score: response.data.score,
        correctAnswers: response.data.correctAnswers,
        totalQuestions: response.data.totalQuestions,
        totalScore: response.data.totalQuestions * 5,
        passed: response.data.passed,
        timeSpent: response.data.timeSpent,
      });
      clearExerciseDraft(sessionId);
      setOpenFinalScoreDialog(true);
      toast.success("Exercise completed successfully!");
    } catch (error) {
      console.error("Failed to complete session:", error);
      toast.error("Failed to complete exercise");
    }
  }, [sessionId, completeSession]);

  const handleNext = useCallback(async () => {
    // Submit answers for ALL questions in the current group before moving
    // This ensures even unanswered questions are recorded as blank
    if (!showInstructions && currentGroup?.questions && sessionId) {
      // Use ref to get the latest answers (avoid stale state)
      const latestAnswers = answersRef.current;
      
      // Check if this is a dialogue group
      const parentQuestion = currentGroup.questions.find(q => q.type === 'dialogue-group');
      
      if (parentQuestion) {
        // Handle dialogue group submission
        const dialogueKey = `dialogue_${parentQuestion._id}`;
        const dialogueAnswers = latestAnswers[dialogueKey] || {};
        
        // Always submit blank answers (empty object if unanswered)
        try {
          await handleSubmitAnswer(parentQuestion._id, dialogueAnswers.blanks || {});
        } catch (error) {
          console.error("Failed to submit dialogue blanks:", error);
        }
        
        // Submit child question answers (null if unanswered)
        const childQuestions = currentGroup.childQuestions || parentQuestion.childQuestions || [];
        for (const child of childQuestions) {
          const childAnswer = dialogueAnswers.childQuestions?.[child._id] || null;
          try {
            await handleSubmitAnswer(child._id, childAnswer);
          } catch (error) {
            console.error("Failed to submit child question answer:", error);
          }
        }
      } else {
        // Handle regular questions - submit ALL regardless of type or whether answered
        for (const question of currentGroup.questions) {
          const questionId = question._id;
          const questionType = question.type;
          const answer = latestAnswers[questionId];

          // Skip multiple-choice that were already submitted on selection
          if (questionType === "multiple-choice" && answer) {
            continue;
          }

          // Submit answer for all other cases (fill-blank, short_answer, unanswered multiple-choice, etc.)
          try {
            await handleSubmitAnswer(questionId, answer || null);
          } catch (error) {
            console.error("Failed to submit answer:", error);
          }
        }
      }
    }

    // Scroll to top + 100px
    window.scrollTo(0, 200);

    // If showing instructions, skip to questions
    if (showInstructions) {
      setShowInstructions(false);
      setSeenPartInstructions((prev) => [...prev, currentPart?.partNumber]);
      return;
    }

    // Move to next question group
    if (currentGroupIndex < questionGroup.length - 1) {
      setCurrentGroupIndex(currentGroupIndex + 1);
    } else if (currentPartIndex < parts.length - 1) {
      // Move to next part
      setCurrentPartIndex(currentPartIndex + 1);
      setCurrentGroupIndex(0);
      setShowInstructions(true);
    } else {
      // Finished all questions
      handleFinishExercise();
    }
  }, [showInstructions, currentGroup, sessionId, currentGroupIndex, questionGroup.length, currentPartIndex, parts.length, currentPart?.partNumber, handleSubmitAnswer, handleFinishExercise]);

  const handlePrevious = useCallback(() => {
    // Scroll to top
    window.scrollTo(0, 200);

    // If showing instructions, go to previous part
    if (showInstructions) {
      if (currentPartIndex > 0) {
        setCurrentPartIndex(currentPartIndex - 1);
        const prevPart = parts[currentPartIndex - 1];
        setCurrentGroupIndex(prevPart.questionGroups.length - 1);
        setShowInstructions(false);
      }
      return;
    }

    // Move to previous question group
    if (currentGroupIndex > 0) {
      setCurrentGroupIndex(currentGroupIndex - 1);
    } else if (currentPartIndex > 0) {
      setCurrentPartIndex(currentPartIndex - 1);
      const prevPart = parts[currentPartIndex - 1];
      setCurrentGroupIndex(prevPart.questionGroups.length - 1);
    }
  }, [showInstructions, currentPartIndex, currentGroupIndex, parts]);

  const getTotalQuestions = useCallback(() => {
    return parts.reduce((sum, part) => {
      const groups = part?.questionGroups || [];
      return (
        sum +
        groups.reduce((groupSum, group) => {
          const childCount = group?.childQuestions?.length || 0;
          if (childCount > 0) return groupSum + childCount;
          return groupSum + (group?.questions?.length || 0);
        }, 0)
      );
    }, 0);
  }, [parts]);

  const getAnsweredQuestions = useCallback(() => {
    return Object.keys(answers).length;
  }, [answers]);

  if (!sessionStarted && !sessionError) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" py={8}>
        <CircularProgress />
      </Box>
    );
  }

  if (sessionError || parts.length === 0) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {sessionError
          ? "Failed to load questions"
          : "No questions available for this section"}
      </Alert>
    );
  }

  // Common props for both LC and RC sessions
  const commonProps = {
    parts,
    currentPartIndex,
    currentGroupIndex,
    showInstructions,
    answers,
    sectionInfo,
    dayLabel,
    onAnswerSelect: handleAnswerSelect,
    onNext: handleNext,
    onPrevious: handlePrevious,
    getTotalQuestions,
    getAnsweredQuestions,
    seenPartInstructions,
  };

  return (
    <Box>
      {/* Render LC or RC session based on section type */}
      {isListening ? (
        <LCExerciseSession {...commonProps} />
      ) : (
        <RCExerciseSession {...commonProps} />
      )}

      {/* Final Score Dialog */}
      <FinalScoreDialog
        open={openFinalScoreDialog}
        score={finalScore?.score}
        correctAnswers={finalScore?.correctAnswers}
        totalQuestions={finalScore?.totalQuestions}
        totalScore={finalScore?.totalScore}
        sessionId={sessionId}
        dayLabel={dayLabel}
        sectionLabel={sectionInfo?.title}
        onClose={() => {
          setOpenFinalScoreDialog(false);
          onExit();
        }}
      />
    </Box>
  );
};

export default ExerciseSessionInline;
